import { ConfirmationDialog, FormattedText } from "client/Components";
import { useDataInputDialog } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useCallback } from "react";

export type DeleteContactConfirmationDialogProps = {
	contactDisplayName: string;
	onConfirm(): void;
	onCancel(): void;
	trackingId: string;
};

export const DeleteContactConfirmationDialog = (props: DeleteContactConfirmationDialogProps): ReactElement => {
	const {
		contactDisplayName,
		onConfirm,
		onCancel,
		trackingId
	} = props;

	return (
		<ConfirmationDialog
			title={
				<FormattedText
					text={localize("_AreYouSureYouWantToDeleteFullName")}
					thingsToReplace={[
						{ what: "{FullName}", filler: contactDisplayName }
					]}
				/>
			}
			confirmText={localize("_Delete")}
			onConfirmation={onConfirm}
			onCancellation={onCancel}
			trackingId={trackingId}
		>
			{localize("_ThisContactWillBePermanentlyDeletedFromYourContacts")}
		</ConfirmationDialog>
	);
};
DeleteContactConfirmationDialog.displayName = "DeleteContactConfirmationDialog";

export type DeleteContactConfirmationDialogInitialData = Omit<
	DeleteContactConfirmationDialogProps,
	"onConfirm" | "onCancel"
>;

export function useDeleteContactConfirmationDialog(): [
	element: ReactElement | null,
	open: (initialData: DeleteContactConfirmationDialogInitialData) => Promise<boolean>
] {
	const [
		element,
		originalOpen
	] = useDataInputDialog<DeleteContactConfirmationDialogInitialData, void>(
		(initialData, onCancel, onConfirm) => (
			<DeleteContactConfirmationDialog
				{...initialData}
				onConfirm={onConfirm}
				onCancel={onCancel}
			/>
		)
	);

	const open = useCallback(
		async (initialData: DeleteContactConfirmationDialogInitialData): Promise<boolean> => {
			return (await originalOpen(initialData))
				.wasSuccessful;
		},
		[originalOpen]
	);

	return [
		element,
		open
	];
}